import Axios from 'axios';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import * as Routes from '@/modules/routes';

export interface SlideStatus {
  completed_at: string | null;
  completed_by_name: string | null;
  created_at: string;
  id: number;
  note: string | null;
  slide_id: number;
  staffer_id: number;
  staffer_name: string;
  status: string;
}

export interface NoteValues {
  note: string;
  status: string;
}

export const slideStatusesKey = (slideId: number) => ['slideStatuses', slideId];

// initialData comes from the ERB row, so the page renders every slide's status with no request;
// the list only refetches after a write. A row added after load passes none and fetches.
// AIDEV-NOTE: Without initialData every mount refetches: the shared client never refetches on mount,
// so the builder drawer would otherwise reopen on a cached list that misses notes added elsewhere.
export const useSlideStatuses = (
  slideId: number,
  initialData?: SlideStatus[],
  enabled = true,
) => useQuery<SlideStatus[]>({
  enabled,
  initialData,
  queryFn: () => Axios
    .get(Routes.plato_api_slide_statuses_path({ for_slide: slideId, includes: ['completed_by', 'staffer'] }))
    .then(res => res.data.data),
  queryKey: slideStatusesKey(slideId),
  refetchOnMount: initialData ? false : 'always',
});

export const useSlideStatusMutations = (slideId: number) => {
  const queryClient = useQueryClient();
  const refresh = () => queryClient.invalidateQueries({ queryKey: slideStatusesKey(slideId) });

  const create = useMutation({
    mutationFn: (values: NoteValues) => Axios.post(Routes.plato_api_slide_statuses_path(), {
      note: values.note || null, slide_id: slideId, status: values.status,
    }),
    onSuccess: refresh,
  });

  const update = useMutation({
    mutationFn: ({ id, ...values }: NoteValues & { id: number }) => Axios.patch(
      Routes.plato_api_slide_status_path(id),
      { note: values.note || null, status: values.status },
    ),
    onSuccess: refresh,
  });

  const destroy = useMutation({
    mutationFn: (id: number) => Axios.delete(Routes.plato_api_slide_status_path(id)),
    onSuccess: refresh,
  });

  const setDone = useMutation({
    mutationFn: ({ done, id }: { done: boolean; id: number }) => Axios.patch(Routes.plato_api_slide_status_path(id), { done }),
    onSuccess: refresh,
  });

  return {
    create, destroy, setDone, update,
  };
};
